'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

/**
 * Batch page generation via the workflow engine (story-entities-redesign §3):
 * one 'book-pages' workflow run instead of a browser-driven per-page loop.
 * The run's startKey is persisted in books.pages_run_id so a remounted UI
 * re-attaches via useWorkflowRun({ startKey }).
 */

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

export async function generateAllPages(input: {
  bookId: string;
  pageNumbers?: number[];
}): Promise<ActionResult<{ startKey: string | null; jobCount: number }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { IllustrationGenerationService } = await import('@/contexts/illustrations/services/BookGenerationService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { getImageGenerationQueue } = await import('@/contexts/ai/infrastructure/imageGenerationQueue');

    const service = new IllustrationGenerationService(
      getIllustrationRepository(),
      getCharacterRepository(),
      getTokenManager(),
      getImageGenerationQueue(),
    );

    const result = await service.startPagesBatch({
      bookId: input.bookId,
      familyId,
      pageNumbers: input.pageNumbers,
    });

    if (!result.success) {
      // A book whose cast was never analysed must not be drawn (gap 6): the
      // caller offers to re-run the pipeline, which starts WITH the analysis.
      if (result.error === 'CAST_NOT_ANALYSED') {
        return { success: false, error: 'CAST_NOT_ANALYSED', code: 'CAST_NOT_ANALYSED' };
      }
      return { success: false, error: result.error || 'Batch generation failed' };
    }
    return {
      success: true,
      data: { startKey: result.startKey ?? null, jobCount: result.jobCount },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generateAllPages', e) };
  }
}
